import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { useTravelText } from '../../app/TravelLocaleContext';
import JourneyActivityMapSurface from './JourneyActivityMapSurface';
import {
  MARKER_KIND_STAY,
} from './journeySpatialMarkerModel';
import {
  SPATIAL_FILTER_ALL,
  SPATIAL_PHASE,
  buildOverviewSpatialMapMarkers,
  buildSpatialCompanionGroups,
  buildSpatialFilterOptions,
  countCompanionActivities,
  filterSpatialCompanionGroups,
  resolveSpatialSelectionAfterFilter,
} from './journeySpatialOverviewModel';
import {
  DEFAULT_SPATIAL_LAYERS,
  SPATIAL_LAYER_ACTIVITY,
  SPATIAL_LAYER_STAY,
  buildTransportLogisticsRows,
  filterMarkersBySpatialLayers,
} from './journeySpatialLogisticsModel';
import './journey-spatial.css';

const MAP = 'travel.journeyWorkspace.map';

/**
 * AUTH59 + Stay Pins + TRAVEL-MAP-STAYS-TRANSPORT-INTEGRATION-01
 * Overview spatial：filter／companion／layer／transport 文字／map。
 */
export default function JourneySpatialOverviewSection({
  journeyId,
  spatialPhase,
  schedule,
  accommodations = [],
  transports = [],
  offlineCached = false,
}) {
  const { t, tf } = useTravelText();
  const [filterKey, setFilterKey] = useState(SPATIAL_FILTER_ALL);
  const [selectedMarkerKey, setSelectedMarkerKey] = useState('');
  const [layers, setLayers] = useState(() => ({ ...DEFAULT_SPATIAL_LAYERS }));
  const companionItemRefs = useRef({});
  const skipScrollRef = useRef(false);

  const offlineWarmed = offlineCached && Boolean(schedule);
  const offlineUnknown = offlineCached && !schedule;
  const companionEnabled = Boolean(schedule) && (
    spatialPhase === SPATIAL_PHASE.READY
    || spatialPhase === SPATIAL_PHASE.OFFLINE
  );

  const stays = useMemo(
    () => (Array.isArray(accommodations) ? accommodations : []),
    [accommodations],
  );
  const transportRows = useMemo(
    () => buildTransportLogisticsRows(transports),
    [transports],
  );

  const allCompanionGroups = useMemo(
    () => (schedule ? buildSpatialCompanionGroups(schedule, stays) : []),
    [schedule, stays],
  );
  const filterOptions = useMemo(
    () => (schedule ? buildSpatialFilterOptions(schedule, stays) : [{
      key: SPATIAL_FILTER_ALL,
      label: '',
      kind: 'all',
    }]),
    [schedule, stays],
  );

  // schedule 重載後若目前 filter 不存在，回到全部
  useEffect(() => {
    if (!filterOptions.some((option) => option.key === filterKey)) {
      setFilterKey(SPATIAL_FILTER_ALL);
    }
  }, [filterOptions, filterKey]);

  const activeFilterMeta = useMemo(
    () => filterOptions.find((option) => option.key === filterKey) || null,
    [filterOptions, filterKey],
  );

  const companionGroups = useMemo(
    () => filterSpatialCompanionGroups(allCompanionGroups, filterKey, stays, activeFilterMeta),
    [allCompanionGroups, filterKey, stays, activeFilterMeta],
  );
  const mapMarkers = useMemo(
    () => (schedule
      ? buildOverviewSpatialMapMarkers(schedule, filterKey, stays, activeFilterMeta)
      : []),
    [schedule, filterKey, stays, activeFilterMeta],
  );
  const visibleMapMarkers = useMemo(
    () => filterMarkersBySpatialLayers(mapMarkers, layers),
    [mapMarkers, layers],
  );
  const totalItemCount = countCompanionActivities(allCompanionGroups);
  const filteredItemCount = countCompanionActivities(companionGroups);
  const planningHref = `/journeys/${journeyId}/planning`;
  const staysHref = `/journeys/${journeyId}/stays`;
  const transportHref = `/journeys/${journeyId}/transport`;

  useEffect(() => {
    setSelectedMarkerKey((current) => (
      resolveSpatialSelectionAfterFilter(current, companionGroups)
    ));
  }, [companionGroups]);

  useEffect(() => {
    if (!selectedMarkerKey || skipScrollRef.current) {
      skipScrollRef.current = false;
      return undefined;
    }
    const node = companionItemRefs.current[selectedMarkerKey];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    return undefined;
  }, [selectedMarkerKey, filterKey]);

  function onFilterChange(nextKey) {
    if (nextKey === filterKey) return;
    skipScrollRef.current = true;
    setFilterKey(nextKey);
  }

  function onMarkerSelect(markerKey) {
    const key = markerKey == null ? '' : String(markerKey);
    setSelectedMarkerKey(key);
  }

  function onCompanionSelect(item) {
    skipScrollRef.current = true;
    setSelectedMarkerKey(String(item.selectionKey || item.activityId || ''));
  }

  function toggleLayer(layerKey) {
    setLayers((current) => ({
      ...current,
      [layerKey]: !current[layerKey],
    }));
  }

  let statusKey = null;
  if (offlineUnknown) {
    // UNKNOWN ≠ EMPTY：舊 {journey} 快照未暖 schedule
    statusKey = `${MAP}.status.offlineUnknown`;
  } else if (spatialPhase === SPATIAL_PHASE.LOADING && !offlineCached) {
    statusKey = `${MAP}.status.loading`;
  } else if (spatialPhase === SPATIAL_PHASE.UNAVAILABLE && !offlineCached) {
    statusKey = `${MAP}.status.unavailable`;
  } else if (companionEnabled && totalItemCount === 0 && transportRows.length === 0) {
    statusKey = `${MAP}.status.empty`;
  } else if (companionEnabled && filteredItemCount > 0 && mapMarkers.length === 0 && !offlineCached) {
    statusKey = filterKey === SPATIAL_FILTER_ALL
      ? `${MAP}.status.noCoordinates`
      : `${MAP}.status.noCoordinatesFiltered`;
  } else if (
    companionEnabled
    && mapMarkers.length > 0
    && visibleMapMarkers.length === 0
    && !offlineCached
  ) {
    statusKey = `${MAP}.status.layersOff`;
  }
  const statusCopy = statusKey ? t(statusKey) : null;

  /** 篩選標籤：全部／住宿／未排程與「第 N 天」依語系；無序號的日標題沿用 model 文字。 */
  function filterLabel(option) {
    if (option.kind === 'all') return t(`${MAP}.filter.all`);
    if (option.kind === 'stays') return t(`${MAP}.filter.stays`);
    if (option.kind === 'unscheduled') return t('travel.publicJourney.unscheduled');
    if (option.kind === 'day' && option.dayNumber > 0) return tf('travel.publicJourney.dayHeading', { number: option.dayNumber });
    return option.label;
  }

  const showFilters = companionEnabled && totalItemCount > 0;
  const showLayers = spatialPhase === SPATIAL_PHASE.READY
    && !offlineCached
    && mapMarkers.length > 0;
  const showMap = spatialPhase === SPATIAL_PHASE.READY
    && !offlineCached
    && visibleMapMarkers.length > 0;
  const showCompanion = companionEnabled && filteredItemCount > 0;
  const showTransport = transportRows.length > 0;
  const showMapOfflineNotice = offlineWarmed;

  return (
    <section
      className="journey-spatial-overview"
      aria-labelledby="journey-spatial-overview-heading"
      data-testid="journey-spatial-overview"
      data-spatial-phase={spatialPhase}
      data-spatial-filter={filterKey}
      data-offline-warmed={offlineWarmed ? 'true' : 'false'}
      data-offline-unknown={offlineUnknown ? 'true' : 'false'}
    >
      <div className="journey-spatial-overview__head">
        <h3 id="journey-spatial-overview-heading">{t(`${MAP}.title`)}</h3>
        <p className="journey-spatial-overview__hint">
          {t(`${MAP}.hint`)}
        </p>
      </div>

      {statusCopy ? (
        <p className="journey-spatial-overview__status" role="status" data-testid="journey-spatial-status">
          {statusCopy}
        </p>
      ) : null}

      {showFilters ? (
        <div
          className="journey-spatial-overview__filters"
          role="group"
          aria-label={t(`${MAP}.filters`)}
          data-testid="journey-spatial-filters"
        >
          {filterOptions.map((option) => {
            const selected = option.key === filterKey;
            return (
              <button
                key={option.key}
                type="button"
                className={`journey-spatial-overview__filter${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                data-testid={`journey-spatial-filter-${option.key}`}
                data-filter-kind={option.kind}
                onClick={() => onFilterChange(option.key)}
              >
                {filterLabel(option)}
                {selected ? <span className="journey-spatial-overview__filter-current">{t(`${MAP}.current`)}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {showLayers ? (
        <div
          className="journey-spatial-overview__layers"
          role="group"
          aria-label={t(`${MAP}.layers`)}
          data-testid="journey-spatial-layers"
        >
          <span className="journey-spatial-overview__layers-label">{t(`${MAP}.layers`)}</span>
          <button
            type="button"
            className={`journey-spatial-overview__layer${layers[SPATIAL_LAYER_ACTIVITY] ? ' is-on' : ''}`}
            aria-pressed={layers[SPATIAL_LAYER_ACTIVITY]}
            data-testid="journey-spatial-layer-activity"
            onClick={() => toggleLayer(SPATIAL_LAYER_ACTIVITY)}
          >
            {t(`${MAP}.layer.activity`)}
          </button>
          <button
            type="button"
            className={`journey-spatial-overview__layer${layers[SPATIAL_LAYER_STAY] ? ' is-on' : ''}`}
            aria-pressed={layers[SPATIAL_LAYER_STAY]}
            data-testid="journey-spatial-layer-stay"
            onClick={() => toggleLayer(SPATIAL_LAYER_STAY)}
          >
            {t(`${MAP}.layer.stay`)}
          </button>
          <p className="journey-spatial-overview__layers-note">
            {t(`${MAP}.legend`)}
          </p>
        </div>
      ) : null}

      {showCompanion ? (
        <div
          className="journey-spatial-overview__companion"
          data-testid="journey-spatial-companion"
        >
          <h4 className="journey-spatial-overview__companion-title">{t(`${MAP}.listTitle`)}</h4>
          {companionGroups.map((group) => (
            <section
              key={group.key}
              className="journey-spatial-overview__group"
              aria-label={group.dayLabel}
              data-unscheduled={group.unscheduled ? 'true' : 'false'}
              data-group-key={group.key}
            >
              <h5 className="journey-spatial-overview__group-heading">{group.dayLabel}</h5>
              <ul className="journey-spatial-overview__list">
                {group.items.map((item) => {
                  const selectionKey = String(item.selectionKey || item.activityId || '');
                  const selected = selectionKey === selectedMarkerKey;
                  const isStay = item.kind === MARKER_KIND_STAY;
                  return (
                    <li key={selectionKey} className="journey-spatial-overview__item-wrap">
                      <button
                        type="button"
                        className={`journey-spatial-overview__item${selected ? ' is-selected' : ''}`}
                        data-kind={item.kind || 'ACTIVITY'}
                        data-activity-id={item.activityId || undefined}
                        data-accommodation-id={item.accommodationId || undefined}
                        data-selection-key={selectionKey}
                        data-has-coordinates={item.hasCoordinates ? 'true' : 'false'}
                        data-status={item.status}
                        data-testid={`journey-spatial-companion-${selectionKey}`}
                        aria-pressed={selected}
                        aria-current={selected ? 'true' : undefined}
                        ref={(node) => {
                          if (node) companionItemRefs.current[selectionKey] = node;
                          else delete companionItemRefs.current[selectionKey];
                        }}
                        onClick={() => onCompanionSelect(item)}
                      >
                        <span className="journey-spatial-overview__item-title">
                          {item.title}
                          {selected ? (
                            <span className="journey-spatial-overview__selected-label">{t(`${MAP}.selected`)}</span>
                          ) : null}
                        </span>
                        {isStay ? (
                          <span className="journey-spatial-overview__badge">{t(`${MAP}.badge.stay`)}</span>
                        ) : item.isCancelled ? (
                          <span
                            className="journey-spatial-overview__badge"
                            data-testid={`spatial-cancelled-${item.activityId}`}
                          >
                            {t(`${MAP}.badge.cancelled`)}
                          </span>
                        ) : (item.statusLabel ? (
                          <span className="journey-spatial-overview__badge">{item.statusLabel}</span>
                        ) : null)}
                        <span className="journey-spatial-overview__item-meta">
                          {item.locationAddress || item.locationLabel || t(`${MAP}.noLocation`)}
                          {!item.hasCoordinates ? ` · ${t(`${MAP}.noCoordinates`)}` : ''}
                          {isStay && (item.checkInAt || item.checkOutAt)
                            ? ` · ${[item.checkInAt, item.checkOutAt].filter(Boolean).map((value) => formatDateTime(value)).join(' → ')}`
                            : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : null}

      {showTransport ? (
        <div
          className="journey-spatial-overview__transport"
          data-testid="journey-spatial-transport"
        >
          <h4 className="journey-spatial-overview__companion-title">{t(`${MAP}.transportTitle`)}</h4>
          <p className="journey-spatial-overview__transport-note" role="note">
            {t(`${MAP}.transportNote`)}
          </p>
          <ul className="journey-spatial-overview__list">
            {transportRows.map((row) => (
              <li key={row.transportId} className="journey-spatial-overview__item-wrap">
                <div
                  className="journey-spatial-overview__transport-item"
                  data-testid={`journey-spatial-transport-${row.transportId}`}
                  data-has-coordinates="false"
                >
                  <span className="journey-spatial-overview__item-title">
                    {row.sourceLabel}
                    {' → '}
                    {row.destinationLabel}
                  </span>
                  <span className="journey-spatial-overview__badge">{t(`${MAP}.badge.transport`)}</span>
                  <span className="journey-spatial-overview__item-meta">
                    {row.transportModeLabel}
                    {row.departureDisplay || row.arrivalDisplay
                      ? ` · ${[row.departureDisplay, row.arrivalDisplay].filter(Boolean).join(' → ')}`
                      : ''}
                    {row.carrierText ? ` · ${row.carrierText}` : ''}
                    {` · ${t(`${MAP}.noMarker`)}`}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="journey-spatial-overview__map-slot" data-testid="journey-spatial-map-slot">
        {showMap ? (
          <JourneyActivityMapSurface
            markers={visibleMapMarkers}
            selectedKey={selectedMarkerKey}
            onSelectMarker={onMarkerSelect}
            title={null}
            emptyDescription={t(`${MAP}.mapEmpty`)}
            showLegend
            classPrefix="journey-activity-map"
            testId="journey-spatial-map"
            emptyTestId="journey-spatial-map-empty"
            googleTestId="journey-spatial-map-google"
            loadingTestId="journey-spatial-map-google-loading"
            planeAriaLabel={t(`${MAP}.markersLabel`)}
            mapAriaLabel={t(`${MAP}.title`)}
          />
        ) : null}
        {showMapOfflineNotice ? (
          <p
            className="journey-spatial-overview__map-offline"
            role="status"
            data-testid="journey-spatial-map-offline"
          >
            {t(`${MAP}.offline`)}
          </p>
        ) : null}
      </div>

      <p className="journey-spatial-overview__cta" data-testid="journey-spatial-workspace-links">
        <Link to={planningHref} data-testid="journey-spatial-planning-cta">
          {t(`${MAP}.ctaPlanning`)}
        </Link>
        <Link to={staysHref} data-testid="journey-spatial-stays-cta">
          {t(`${MAP}.ctaStays`)}
        </Link>
        <Link to={transportHref} data-testid="journey-spatial-transport-cta">
          {t(`${MAP}.ctaTransport`)}
        </Link>
      </p>
    </section>
  );
}
